/*
 * Purpose: Opt-in mission automation — auto-connect / auto-deploy (human gate by default)
 * Last modified: 2026-06-28 | Agent
 * Completeness: 95
 */
import { ShieldCheck } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  MISSION_AUTOMATION_SCOPE_LABELS,
  type MissionAutomationScope,
} from '@/lib/missionAutomationPolicy';
import { useMissionKnowledgeStore } from '@/stores/missionKnowledgeStore';
import { cn } from '@/lib/utils';

const SCOPES: MissionAutomationScope[] = ['auto_connect', 'auto_deploy'];

interface MissionAutomationConsentPanelProps {
  className?: string;
}

export function MissionAutomationConsentPanel({ className }: MissionAutomationConsentPanelProps) {
  const automationConsent = useMissionKnowledgeStore((s) => s.automationConsent);
  const setAutomationScope = useMissionKnowledgeStore((s) => s.setAutomationScope);

  return (
    <div
      className={cn(
        'rounded-md border border-border/60 bg-muted/30 p-3 space-y-3',
        className
      )}
    >
      <div className="flex items-start gap-2">
        <ShieldCheck className="h-4 w-4 text-primary shrink-0 mt-0.5" aria-hidden />
        <div className="min-w-0">
          <p className="text-xs font-semibold text-foreground">Mission automation (optional)</p>
          <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
            The AI may propose anything needed for this Mission. <strong>Auto-connect</strong> and{' '}
            <strong>auto-deploy</strong> opt-in only skips repeat prompts for lower-risk steps —{' '}
            <strong>elevated and critical items still need your approval</strong> on the review panel.
          </p>
        </div>
      </div>

      <ul className="space-y-2">
        {SCOPES.map((scope) => {
          const meta = MISSION_AUTOMATION_SCOPE_LABELS[scope];
          const checked = automationConsent.scopes[scope]?.enabled === true;
          const id = `mission-automation-${scope}`;
          return (
            <li key={scope} className="flex items-start gap-2.5 rounded-md border border-border/40 bg-background/60 px-2.5 py-2">
              <Checkbox
                id={id}
                checked={checked}
                onCheckedChange={(v) => setAutomationScope(scope, v === true)}
                className="mt-0.5"
              />
              <Label htmlFor={id} className="text-[11px] font-normal leading-relaxed cursor-pointer">
                <span className="font-medium text-foreground">{meta.title}</span>
                <span className="block text-muted-foreground mt-0.5">{meta.description}</span>
              </Label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
